/**
 * Supabase Report Service
 * Provides functions to interact with the reports table in Supabase
 */

import { supabase } from './client';

export interface SupabaseReport {
  id: string;
  legacy_id: string;
  id_group?: string;
  period?: string;
  report_date?: string;
  id_team?: string;
  report_name?: string;
  summary?: string;
  file_url?: string;
  created_by_user_id?: string;
  created_on?: string;
  bod_comment?: string;
  file_url_temp?: string;
  /** Link Google Drive — chỉ còn ở báo cáo cũ (trước 2026-08-24) */
  full_url?: string;
  /** ID file trên Google Drive — chỉ còn ở báo cáo cũ */
  file_id?: string;
  /** Đường dẫn file gốc trong bucket `report-files` — báo cáo mới dùng cái này */
  supabase_file_path?: string;
  /** Đường dẫn bản AI trong bucket `report-files` */
  ai_storage_path?: string;
  /** Link Drive của bản AI — chỉ còn ở báo cáo cũ */
  ai_version_url?: string;
  summary_by_ai?: string;
  auth_user_id?: string;
  /** Chủ sở hữu trong auth.users (đồng bộ với auth_user_id) */
  user_id?: string;
  is_summary?: boolean;
  sync_source?: string;
  synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ReportFilters {
  period?: string;
  periods?: string[]; // Support multiple periods
  id_team?: string;
  created_by_user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string; // Search in report_name and summary
}

export interface PaginationOptions {
  page?: number;
  limit?: number;
}

export interface ReportResponse {
  data: SupabaseReport[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PeriodPaginationOptions {
  page?: number;
  periodsPerPage?: number; // Default: 4
}

export interface PeriodPaginationResult {
  periods: string[];           // Current page periods ["W2531", "W2530", "W2529", "W2528"]
  currentPage: number;         
  totalPages: number;          
  totalPeriods: number;        
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PageReportsResult {
  reports: SupabaseReport[];   // All reports for current page's periods
  periods: string[];           // Which periods these reports belong to
  reportCount: number;         // Total reports in this page
}

class ReportService {
  /**
   * Get reports with filtering and pagination
   */
  async getReports(
    filters: ReportFilters = {},
    pagination: PaginationOptions = {}
  ): Promise<ReportResponse> {
    const { page = 1, limit = 50 } = pagination;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('reports')
      .select('*', { count: 'exact' })
      .order('report_date', { ascending: false })
      .order('created_on', { ascending: false });

    // Apply filters
    if (filters.period) {
      query = query.eq('period', filters.period);
    }

    if (filters.periods && filters.periods.length > 0) {
      query = query.in('period', filters.periods);
    }

    if (filters.id_team) {
      query = query.eq('id_team', filters.id_team);
    }

    if (filters.created_by_user_id) {
      query = query.eq('created_by_user_id', filters.created_by_user_id);
    }

    if (filters.date_from) {
      query = query.gte('report_date', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('report_date', filters.date_to);
    }

    if (filters.search) {
      query = query.or(
        `report_name.ilike.%${filters.search}%,summary.ilike.%${filters.search}%,summary_by_ai.ilike.%${filters.search}%`
      );
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      throw error;
    }

    const totalPages = Math.ceil((count || 0) / limit);

    return {
      data: data || [],
      count: count || 0,
      page,
      limit,
      totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }

  /**
   * Get a single report by ID (Supabase UUID)
   */
  async getReportById(id: string): Promise<SupabaseReport | null> {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Report not found
      }
      throw error;
    }

    return data;
  }

  /**
   * Get a single report by legacy ID (SQL Server ID)
   */
  async getReportByLegacyId(legacyId: string): Promise<SupabaseReport | null> {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('legacy_id', legacyId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Report not found
      }
      throw error;
    }

    return data;
  }

  /**
   * Get distinct periods from reports
   */
  async getPeriods(): Promise<string[]> {
    const { data, error } = await supabase
      .from('reports')
      .select('period')
      .not('period', 'is', null)
      .order('period', { ascending: false });

    if (error) {
      throw error;
    }

    // Get unique periods
    const uniquePeriods = [...new Set(data?.map(item => item.period))];
    return uniquePeriods.filter(Boolean) as string[];
  }

  /**
   * Get distinct teams from reports
   */
  async getTeams(): Promise<string[]> {
    const { data, error } = await supabase
      .from('reports')
      .select('id_team')
      .not('id_team', 'is', null)
      .order('id_team');

    if (error) {
      throw error;
    }

    // Get unique teams
    const uniqueTeams = [...new Set(data?.map(item => item.id_team))];
    return uniqueTeams.filter(Boolean) as string[];
  }

  /**
   * 🔥 NEW: Get paginated periods (4 periods per page, Z-A)
   */
  async getPaginatedPeriods(
    pagination: PeriodPaginationOptions = {}
  ): Promise<PeriodPaginationResult> {
    const { page = 1, periodsPerPage = 4 } = pagination;
    
    // Get ALL periods first (already sorted Z-A)
    const allPeriods = await this.getPeriods();
    
    // Calculate pagination
    const totalPeriods = allPeriods.length;
    const totalPages = Math.ceil(totalPeriods / periodsPerPage);
    const offset = (page - 1) * periodsPerPage;
    
    // Get current page periods
    const currentPagePeriods = allPeriods.slice(offset, offset + periodsPerPage);
    
    return {
      periods: currentPagePeriods,
      currentPage: page,
      totalPages: Math.max(1, totalPages),
      totalPeriods,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }

  /**
   * 🔥 NEW: Get ALL reports for specific periods (for current page)
   */
  async getReportsForPeriods(
    periods: string[],
    filters: Omit<ReportFilters, 'period' | 'periods'> = {}
  ): Promise<PageReportsResult> {
    if (periods.length === 0) {
      return {
        reports: [],
        periods: [],
        reportCount: 0,
      };
    }

    let query = supabase
      .from('reports')
      .select('*')
      .in('period', periods)
      .order('period', { ascending: false })      // Z-A by period first
      .order('report_date', { ascending: false }) // Then by date
      .order('created_on', { ascending: false }); // Then by created time

    // Apply filters (excluding period filters since we use periods array)
    if (filters.id_team) {
      query = query.eq('id_team', filters.id_team);
    }
    if (filters.created_by_user_id) {
      query = query.eq('created_by_user_id', filters.created_by_user_id);
    }
    if (filters.date_from) {
      query = query.gte('report_date', filters.date_from);
    }
    if (filters.date_to) {
      query = query.lte('report_date', filters.date_to);
    }
    if (filters.search) {
      query = query.or(
        `report_name.ilike.%${filters.search}%,summary.ilike.%${filters.search}%,summary_by_ai.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;
    
    if (error) {
      throw error;
    }

    return {
      reports: data || [],
      periods,
      reportCount: data?.length || 0,
    };
  }

  /**
   * Get reports statistics
   */
  async getReportsStats(): Promise<{
    totalReports: number;
    reportsWithAI: number;
    uniquePeriods: number;
    uniqueTeams: number;
    latestSyncDate: string | null;
  }> {
    const { data, error } = await supabase.rpc('get_reports_sync_stats', {
      days_back: 30
    });

    if (error) {
      console.warn('Could not fetch sync stats:', error);
    }

    // Get basic stats
    const { count: totalReports } = await supabase
      .from('reports')
      .select('*', { count: 'exact', head: true });

    const { count: reportsWithAI } = await supabase
      .from('reports')
      .select('*', { count: 'exact', head: true })
      .not('summary_by_ai', 'is', null);

    const periods = await this.getPeriods();
    const teams = await this.getTeams();

    const { data: latestSync } = await supabase
      .from('reports')
      .select('synced_at')
      .order('synced_at', { ascending: false })
      .limit(1)
      .single();

    return {
      totalReports: totalReports || 0,
      reportsWithAI: reportsWithAI || 0,
      uniquePeriods: periods.length,
      uniqueTeams: teams.length,
      latestSyncDate: latestSync?.synced_at || null,
    };
  }

  /**
   * Search reports by text
   */
  async searchReports(
    searchTerm: string,
    pagination: PaginationOptions = {}
  ): Promise<ReportResponse> {
    return this.getReports(
      { search: searchTerm },
      pagination
    );
  }

  /**
   * Lightweight query: trả về {total, byGroup} cho tất cả báo cáo của user (không phân trang).
   * Chỉ select id_group để tối ưu băng thông.
   */
  async getMyReportGroupCounts(
    authUserId: string,
    legacyUserId: string | null,
  ): Promise<{ total: number; byGroup: Record<string, number> }> {
    const byAuth = `auth_user_id.eq.${authUserId},user_id.eq.${authUserId}`;
    const orFilter = legacyUserId ? `${byAuth},created_by_user_id.eq.${legacyUserId}` : byAuth;

    const { data, error } = await supabase
      .from('reports')
      .select('id_group')
      .or(orFilter);

    if (error) throw error;

    const byGroup: Record<string, number> = {};
    (data || []).forEach(r => {
      const g = r.id_group ?? '__none__';
      byGroup[g] = (byGroup[g] ?? 0) + 1;
    });
    return { total: data?.length ?? 0, byGroup };
  }

  /**
   * Get distinct periods for a specific user.
   * OR: auth_user_id / user_id hoặc created_by_user_id = legacyId
   * groupId = null → tất cả nhóm; groupId = ID nhóm → chỉ lấy period của nhóm đó.
   */
  async getMyPeriods(
    authUserId: string,
    legacyUserId: string | null,
    groupId?: string | null,
  ): Promise<string[]> {
    const byAuth = `auth_user_id.eq.${authUserId},user_id.eq.${authUserId}`;
    const orFilter = legacyUserId ? `${byAuth},created_by_user_id.eq.${legacyUserId}` : byAuth;

    let query = supabase
      .from('reports')
      .select('period')
      .or(orFilter)
      .not('period', 'is', null)
      .order('period', { ascending: false });

    if (groupId && groupId !== 'all') {
      query = query.eq('id_group', groupId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return [...new Set(data?.map(r => r.period))].filter(Boolean) as string[];
  }

  /**
   * Get reports belonging to a specific user for given periods.
   * OR: auth_user_id / user_id hoặc created_by_user_id = legacyId
   * groupId = null → tất cả nhóm; groupId = ID nhóm → chỉ lấy report của nhóm đó.
   */
  async getMyReports(
    authUserId: string,
    legacyUserId: string | null,
    periods: string[],
    groupId?: string | null,
  ): Promise<SupabaseReport[]> {
    if (periods.length === 0) return [];

    const byAuth = `auth_user_id.eq.${authUserId},user_id.eq.${authUserId}`;
    const orFilter = legacyUserId ? `${byAuth},created_by_user_id.eq.${legacyUserId}` : byAuth;

    let query = supabase
      .from('reports')
      .select('*')
      .or(orFilter)
      .in('period', periods)
      .order('period', { ascending: false })
      .order('report_date', { ascending: false })
      .order('created_on', { ascending: false });

    if (groupId && groupId !== 'all') {
      query = query.eq('id_group', groupId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  /**
   * Get recent reports (last 30 days)
   */
  async getRecentReports(limit: number = 20): Promise<SupabaseReport[]> {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .gte('report_date', thirtyDaysAgo.toISOString())
      .order('report_date', { ascending: false })
      .order('created_on', { ascending: false })  
      .limit(limit);

    if (error) {
      throw error;
    }

    return data || [];
  }
}

export const reportService = new ReportService();
export default reportService;